import Explorer, { FormRow } from "../base/explorer.js"
import {
  Button,
  Table,
  EmptyTableRow,
  Actions,
  Header,
  Datepicker,
  Dropdown,
  Input,
} from "widgets"
import { useMemo, useCallback, useEffect, useContext, useState } from "react"
import { explorerTableStyle } from "common-styles"
import { AppContext } from "../base/app-context-provider.js"
import {
  useService,
  formatDate,
  useObjectStatuses,
  dateWithoutTime,
  getDaysBefore,
} from "utils"
import { readAgencyRequests } from "./help-desk-services.js"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useFormContext } from "react-hook-form"
import { formatDateAndTime } from "../../utils.js"

const tableColumns = () => [
  t("help_desk.request_date_time"),
  t("help_desk.order_id"),
  t("help_desk.service"),
  t("help_desk.request_type"),
  t("help_desk.response_date_time"),
  t("help_desk.action_status"),
  t("action"),
]

const SearchAgencyRequestsLayout = ({ isReadPending }) => {
  const { watch } = useFormContext()
  const values = watch()
  const { filterObjectStatuses, isReadObjectStatusesPending } =
    useObjectStatuses()

  const validationRules = useMemo(
    () => ({
      fromDate: {
        validate: (value) =>
          !values.toDate ||
          !value ||
          dateWithoutTime(value) <= dateWithoutTime(values.toDate) ||
          "From Date must be lesser or the same as To Date",
      },
      toDate: {
        validate: (value) =>
          !values.fromDate ||
          !value ||
          dateWithoutTime(value) >= dateWithoutTime(values.fromDate) ||
          "To Date must be greater or the same as From Date",
      },
    }),
    [values],
  )

  return (
    <>
      <FormRow>
        <Datepicker
          fieldName="fromDate"
          className="from-date"
          label={t("date_from")}
          placeholder={t("date_from")}
          rules={validationRules["fromDate"]}
          maxDate={Date.now()}
        />
        <Datepicker
          fieldName="toDate"
          name="to-date"
          className="to-date"
          label={t("date_to")}
          placeholder={t("date_to")}
          rules={validationRules["toDate"]}
          maxDate={Date.now()}
        />
        <Input
          className="orderNo"
          css={{ flexGrow: 2 }}
          fieldName="orderNo"
          label={t("help_desk.order_id")}
        />
        <Dropdown
          label={t("help_desk.action_status")}
          fieldName="actionStatusId"
          className="action-status-id"
          isLoading={isReadObjectStatusesPending}
          options={filterObjectStatuses(["refunded", "rejected"])}
          placeholder={t("help_desk.action_status")}
        />
      </FormRow>
      <Button
        className="search-button"
        title={t("search")}
        css={{ alignSelf: "flex-end" }}
        type="submit"
        disabled={isReadPending}
      />
    </>
  )
}

const AgencyRequestsTable = ({ agencyRequests, isReadPending }) => {
  const navigate = useNavigate()
  const actions = useMemo(
    () =>
      _.compact([
        {
          type: t("view"),
          callback: ({ request }) =>
            navigate("/visa/view-my-cancel-transaction-request", {
              state: request,
            }),
        },
      ]),
    [navigate],
  )

  return (
    <Table
      className="agency-requests-table"
      total={agencyRequests.count}
      css={explorerTableStyle}
      isPending={isReadPending}
    >
      <Header columns={tableColumns()} />
      <tbody>
        {_.map(agencyRequests.data, (request = {}, key) => (
          <tr key={key}>
            <td>{formatDate(request.requestDatetime)}</td>
            <td>{request.orderNo}</td>
            <td>{request.service}</td>
            <td>{request.requestType}</td>
            <td>{formatDateAndTime(request.responseDatetime)}</td>
            <td>{request.responseActionStatusName}</td>
            <td>
              <Actions actions={actions} request={request} />
            </td>
          </tr>
        ))}
        <EmptyTableRow
          isPending={isReadPending}
          {...agencyRequests}
          colSpan={_.size(tableColumns())}
        />
      </tbody>
    </Table>
  )
}

const MyRequests = () => {
  const [agencyRequests = {}, isReadPending, startReadAgencyRequests] =
    useService(readAgencyRequests)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [formData, setFormData] = useState()
  const {
    user: { officeBasicDTO },
  } = useContext(AppContext)

  const defaultValues = useMemo(
    () => ({
      toDate: Date.now(),
      fromDate: getDaysBefore(30),
      requesterOfficeId: officeBasicDTO.id,
    }),
    [officeBasicDTO.id],
  )

  const query = useMemo(
    () =>
      _.merge(
        { page: 0, size: 10 },
        defaultValues,
        formData,
        Object.fromEntries(searchParams.entries()),
      ),
    [formData, defaultValues, searchParams],
  )

  const onSearch = useCallback(
    ({ data }) => {
      setFormData(data)
      startReadAgencyRequests({
        query: _.merge(
          {},
          query,
          Object.fromEntries(searchParams.entries()),
          data,
        ),
      })
    },
    [query, searchParams, startReadAgencyRequests],
  )

  useEffect(() => {
    startReadAgencyRequests({ query })
  }, [query, startReadAgencyRequests])

  return (
    <Explorer
      className="my-requests"
      title={t("help_desk.my_requests")}
      formHeading={t("help_desk.search_my_requests")}
      onSubmit={onSearch}
      onClose={() => navigate("/")}
      defaultValues={defaultValues}
      Component={
        <AgencyRequestsTable
          agencyRequests={agencyRequests}
          isReadPending={isReadPending}
        />
      }
    >
      <SearchAgencyRequestsLayout isReadPending={isReadPending} />
    </Explorer>
  )
}

export default MyRequests
